import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { startHeroStatsAnimation } from "../index.js";
import { Handshake, Dices, Ticket, Shuffle, Trophy } from "lucide-react";

const HeroStats = ({
  bookies,
  lotteries,
  buytotal,
  draws,
  wins,
  startAnimation,
}) => {
  const [displayValues, setDisplayValues] = useState([0, 0, 0, 0, 0]);

  const stats = [
    { title: "Creators", value: bookies, icon: Handshake },
    { title: "Lotteries", value: lotteries, icon: Dices },
    { title: "Tickets Sold", value: buytotal, icon: Ticket },
    { title: "Draws", value: draws, icon: Shuffle },
    { title: "Wins", value: wins, icon: Trophy },
  ];

  useEffect(() => {
    if (!startAnimation) {
      setDisplayValues([
        Number(bookies) || 0,
        Number(lotteries) || 0,
        Number(buytotal) || 0,
        Number(draws) || 0,
        Number(wins) || 0,
      ]);
      return;
    }

    const cleanup = startHeroStatsAnimation(stats, setDisplayValues);
    return cleanup;
  }, [startAnimation, bookies, lotteries, buytotal, draws, wins]);

  return (
    <Box
      className="home-hero-stats"
      sx={{
        display: { xs: "grid", sm: "flex" },
        gridTemplateColumns: {
          xs: "repeat(6, minmax(0, 1fr))",
        },
        flexDirection: { sm: "row" },
        justifyContent: { sm: "flex-start" },
        alignItems: { sm: "center" },
        gap: { xs: "8px", sm: 0 },
        width: { xs: "100%", sm: "fit-content" },
        maxWidth: { xs: "850px", sm: "100%" },
        mt: { xs: 1, sm: 1.25, md: 1.5 },
        mb: { xs: 1, sm: 0.5, md: 0.75 },
      }}
    >
      {stats.map((stat, index) => {
        const Icon = stat.icon;

        return (
          <Box
            key={stat.title}
            className="hero-stat-item"
            sx={{
              gridColumn: {
                xs:
                  index < 3
                    ? `${index * 2 + 1} / span 2`
                    : index === 3
                    ? "2 / span 2"
                    : "4 / span 2",
              },
              position: "relative",
              minWidth: 0,
              minHeight: { xs: "78px", sm: "auto" },
              pl: { xs: "5px", sm: "14px", md: "18px" },
              pr: { xs: "5px", sm: "24px", md: "32px" },
              py: { xs: "7px", sm: "12px", md: "16px" },
              borderRadius: { xs: "16px", sm: "0" },
              background: {
                xs: (theme) => theme.custom.colors.alpha.white[82],
                sm: "transparent",
              },
              border: {
                xs: (theme) =>
                  `1px solid ${theme.custom.colors.alpha.white[95]}`,
                sm: "none",
              },
              borderRight: {
                xs: (theme) =>
                  `1px solid ${theme.custom.colors.alpha.white[95]}`,
                sm:
                  index !== stats.length - 1
                    ? "1px solid rgba(133, 143, 163, 0.25)"
                    : "none",
              },
              boxShadow: {
                xs: "0 10px 28px rgba(20, 33, 43, 0.08)",
                sm: "none",
              },
              backdropFilter: { xs: "blur(12px)", sm: "none" },
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: { xs: "center", sm: "flex-start" },
              textAlign: { xs: "center", sm: "left" },
              overflow: "hidden",
              transition: {
                xs: "transform 0.3s ease, box-shadow 0.3s ease",
                sm: "none",
              },
              "&:hover": {
                transform: { xs: "translateY(-6px)", sm: "none" },
                boxShadow: {
                  xs: "0 18px 40px rgba(20, 33, 43, 0.14)",
                  sm: "none",
                },
              },
              "&::after": {
                content: { xs: '""', sm: "none" },
                position: "absolute",
                bottom: 0,
                left: "20%",
                width: "60%",
                height: "3px",
                borderRadius: "10px 10px 0 0",
                background: (theme) => theme.palette.primary.main,
                opacity: 0.8,
              },
            }}
          >
            <Box
              sx={{
                width: { xs: "29px", sm: "auto" },
                height: { xs: "29px", sm: "auto" },
                borderRadius: { xs: "50%", sm: "0" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: { xs: "3px", sm: "0" },
                mr: { sm: "8px", md: "10px" },
                flexShrink: 0,
                color: (theme) => theme.palette.primary.main,
                "& svg": {
                  width: { xs: "19px", sm: "22px", md: "26px" },
                  height: { xs: "19px", sm: "22px", md: "26px" },
                },
              }}
            >
              <Icon strokeWidth={1.8} />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "center", sm: "flex-start" },
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontFamily: "inherit",
                  fontSize: { xs: "28px", sm: "18px", md: "22px" },
                  fontWeight: { xs: 900, sm: 800 },
                  color: (theme) => theme.palette.text.primary,
                  lineHeight: 1,
                  order: { xs: 2, sm: 1 },
                  marginTop: { xs: "-2px", sm: "0" },
                  marginBottom: { sm: "2px" },
                }}
              >
                {displayValues[index]}
              </Typography>

              <Typography
                component="p"
                sx={{
                  margin: 0,
                  fontFamily: "inherit",
                  fontSize: { xs: "10px", sm: "10px", md: "12px" },
                  fontWeight: { xs: 600, sm: 500 },
                  color: (theme) => theme.palette.text.secondary,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  order: { xs: 1, sm: 2 },
                }}
              >
                {stat.title}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default HeroStats;